import type { OrderSetLanguageLayer } from "./types";

/**
 * Content-based language classification. Doc 04 section 5.5.
 *
 * This replaces classification from filename tokens. A filename token worked
 * only because one set existed: the moment a second set arrives whose files
 * carry no `_EN` or `_FR` in the name, every document classifies as `unmarked`
 * and the two layers merge into one set. The layer is a property of the
 * document, so it is read from the document.
 *
 * Two separate questions, deliberately not conflated:
 *
 * 1. **Which layer is this document?** Read from the document's own title
 *    declaration. Doc 04 section 5.5 records that the French-layer files are
 *    English translations of French source, titled "French content — English
 *    translation". That title is the declaration.
 *
 * 2. **Is the body actually French?** The separate detection pass doc 04
 *    section 5.5 requires, on accented character frequency and a short French
 *    stopword list. If it fires, the caller warns rather than delivering,
 *    because no French voice handling exists.
 *
 * The first answers set identity. The second answers whether speech can
 * proceed. A document can be the French layer and have an English body, which
 * is exactly what the current source is.
 */

/**
 * The declaration, in either language. Matched on the title only: the phrase
 * appears in translator's notes in the body of both layers, so matching
 * anywhere would classify an English document as French.
 */
const FRENCH_LAYER_DECLARATION =
  /\bfrench\s+content\b|\bcontenu\s+fran[çc]ais\b|\bversion\s+fran[çc]aise\b/i;

/** Short list, ordinary language. No doctrinal vocabulary is involved. */
const FRENCH_STOPWORDS = new Set([
  "au",
  "aux",
  "avec",
  "ce",
  "ces",
  "cette",
  "dans",
  "de",
  "des",
  "du",
  "elle",
  "en",
  "est",
  "et",
  "être",
  "il",
  "je",
  "la",
  "le",
  "les",
  "leur",
  "leurs",
  "mais",
  "ne",
  "nous",
  "on",
  "ou",
  "par",
  "pas",
  "pour",
  "qui",
  "que",
  "sa",
  "se",
  "ses",
  "son",
  "sont",
  "sur",
  "un",
  "une",
  "vous",
  "à",
]);

const ENGLISH_STOPWORDS = new Set([
  "a",
  "all",
  "and",
  "are",
  "as",
  "at",
  "be",
  "by",
  "for",
  "from",
  "has",
  "have",
  "in",
  "is",
  "it",
  "not",
  "of",
  "on",
  "or",
  "that",
  "the",
  "this",
  "to",
  "will",
  "with",
]);

const ACCENTED = /[àâäçéèêëîïôöùûüÿœæ]/gi;
const WORDS = /[a-zà-ÿœæ']+/gi;

/**
 * Thresholds.
 *
 * Measured across the sixteen Markdown documents of the current corpus, counts
 * only. Accented characters run 0.24 to 0.39 per thousand in the English-layer
 * files and 1.32 to 1.41 in the French-layer files — the difference is French
 * proper nouns, not French prose. French stopwords run 0 to 10 against 14 to
 * 16, which overlaps and is therefore not a usable layer signal on its own.
 *
 * Continuous French prose sits an order of magnitude above both figures. The
 * body thresholds are set well clear of everything the corpus contains, so the
 * pass stays silent on a translated document and fires on a real French one.
 * They are floors chosen not to false-fire, not a tuned classifier.
 */
export const FRENCH_BODY_ACCENTS_PER_THOUSAND = 10;
export const FRENCH_BODY_STOPWORD_SHARE = 0.05;
/** Below this, there is not enough text to call it anything. */
export const MINIMUM_WORDS = 25;
const ENGLISH_STOPWORD_SHARE = 0.05;

export interface LanguageEvidence {
  readonly accentsPerThousand: number;
  readonly declaresFrenchLayer: boolean;
  readonly englishStopwordShare: number;
  readonly frenchStopwordShare: number;
  readonly words: number;
}

export interface LanguageDetection {
  readonly evidence: LanguageEvidence;
  /**
   * Doc 04 section 5.5: the body is French, so delivery must warn rather than
   * speak it. Independent of the layer.
   */
  readonly frenchBody: boolean;
  readonly layer: OrderSetLanguageLayer;
}

function titleOf(text: string): string {
  // The declaration lives in the document title. Take the first non-blank
  // line rather than the first heading, so a document with no heading still
  // offers its title line.
  const trimmed = text.trimStart();
  const end = trimmed.indexOf("\n");
  return end < 0 ? trimmed : trimmed.slice(0, end);
}

function share(words: readonly string[], list: ReadonlySet<string>): number {
  if (words.length === 0) return 0;
  return words.filter((word) => list.has(word)).length / words.length;
}

/**
 * Classifies one document from its content.
 *
 * `null` text means the format carries no readable text — an image, a
 * workbook, an archive. Those have no language and are never guessed at; they
 * come back `unmarked`, which is what they are.
 */
export function detectLanguage(text: string | null): LanguageDetection {
  if (text === null || text.trim() === "") {
    return {
      layer: "unmarked",
      frenchBody: false,
      evidence: {
        accentsPerThousand: 0,
        declaresFrenchLayer: false,
        englishStopwordShare: 0,
        frenchStopwordShare: 0,
        words: 0,
      },
    };
  }

  const words = (text.toLowerCase().match(WORDS) ?? []).map((word) => word);
  const accentsPerThousand =
    ((text.match(ACCENTED) ?? []).length * 1000) / text.length;
  const frenchStopwordShare = share(words, FRENCH_STOPWORDS);
  const englishStopwordShare = share(words, ENGLISH_STOPWORDS);
  const declaresFrenchLayer = FRENCH_LAYER_DECLARATION.test(titleOf(text));

  const evidence: LanguageEvidence = {
    accentsPerThousand,
    declaresFrenchLayer,
    englishStopwordShare,
    frenchStopwordShare,
    words: words.length,
  };

  const frenchBody =
    words.length >= MINIMUM_WORDS &&
    accentsPerThousand >= FRENCH_BODY_ACCENTS_PER_THOUSAND &&
    frenchStopwordShare >= FRENCH_BODY_STOPWORD_SHARE;

  if (declaresFrenchLayer || frenchBody) {
    return { layer: "fr", frenchBody, evidence };
  }
  if (
    words.length >= MINIMUM_WORDS &&
    englishStopwordShare >= ENGLISH_STOPWORD_SHARE
  ) {
    return { layer: "en", frenchBody, evidence };
  }
  // Text that is neither recognisably English nor French, or too short to
  // call. Not a guess in either direction.
  return { layer: "unmarked", frenchBody, evidence };
}
